/**
 * SocketService.js — Phase 1.1: Real-Time Nervous System
 *
 * Socket.IO client with:
 *  - JWT auth in handshake (auto-refresh on reconnect)
 *  - /ops namespace for operations events
 *  - 4-level room hierarchy: org, project, asset, mission
 *  - Typed events: asset:telemetry, asset:alert, mission:update, narrative:event
 *  - Client-side delta filter (suppress speed===0 heartbeats)
 *  - Reactive connection state exposed to Vue components
 */
import { reactive, computed, getCurrentInstance, onUnmounted } from 'vue'
import { logger } from '@sentry/vue'
import { io } from 'socket.io-client'
import { extractCoords } from '@/@core/utils/geo'


/* ─── Config ──────────────────────────────────────────────────────── */
const LIVE_URL = import.meta.env.VITE_LIVE_URL || 'http://localhost:5001'

/* ─── Internal State ──────────────────────────────────────────────── */
let opsSocket = null
const joinedRooms    = new Set()
const roomListeners  = new Map()   // roomId → Map<eventType, Set<callback>>
const globalListeners = new Map()  // eventType → Set<callback>

/* ─── Reactive State (importable by components) ───────────────────── */
export const socketState = reactive({
  connected: false,
  lastEvent: null,
  reconnectAttempt: 0,
  error: null,
})

/* ═══════════════════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════════════════ */

function getToken() {
  return localStorage.getItem('token') || ''
}

function getOrgId() {
  return localStorage.getItem('org_id') || ''
}

/**
 * Room ID builders — enforce naming convention
 */
export const Rooms = {
  org:     (id) => `${id}`,
  project:   (id) => `${id}`,
  asset:   (id) => `${id}`,
  mission: (id) => `${id}`,
}

/**
 * Typed event constants
 */
export const Events = {
  // Device-originated events (from MQTT-Ingestor)
  DEVICE_MEASUREMENT:    'DEVICE_MEASUREMENT',
  DEVICE_EVENT:          'DEVICE_EVENT',
  DEVICE_STATUS:         'DEVICE_STATUS',
  DEVICE_CONFIGURATION:  'DEVICE_CONFIGURATION',
  DEVICE_COMMAND:        'DEVICE_COMMAND',

  MISSION_UPDATE:   'MISSION_UPDATE',
  MISSION_COMPLETE: 'MISSION_COMPLETE',
  NARRATIVE_EVENT:  'NARRATIVE_EVENT',
  AGENT_STATUS:     'AGENT_STATUS',
  ORG_BROADCAST:    'ORG_BROADCAST',
  GEOFENCE_BREACH:  'GEOFENCE_BREACH',
  CAMERA_EVENT:     'CAMERA_EVENT',
  CAMERA_STATUS:    'CAMERA_STATUS',
  COMMAND_ACK:      'COMMAND_ACK',

  // Phase 5
  SAFETY_VIOLATION:    'SAFETY_VIOLATION',
  PRODUCTION_EVENT:    'PRODUCTION_EVENT',
  QUALITY_INSPECTION:  'QUALITY_INSPECTION',
  LINE_STATUS:         'LINE_STATUS',

  // Phase 6
  WORKFLOW_EXECUTION:  'WORKFLOW_EXECUTION',
  INTEGRATION_EVENT:   'INTEGRATION_EVENT',
  ESTIMALO_SYNC:       'ESTIMALO_SYNC',

  // Phase 7
  ENERGY_READING:      'ENERGY_READING',
  ENERGY_ALERT:        'ENERGY_ALERT',
  FLUID_READING:       'FLUID_READING',
  FLUID_ALERT:         'FLUID_ALERT',
  AMBIANCE_READING:    'AMBIANCE_READING',
  ACCESS_EVENT:        'ACCESS_EVENT',
  NETWORK_HEALTH:      'NETWORK_HEALTH',
  NETWORK_ALERT:       'NETWORK_ALERT',

  // Agent Engine — task lifecycle (published via Redis → live-service)
  AGENT_TASK_STARTED:          'AGENT_TASK_STARTED',
  AGENT_TASK_COMPLETED:        'AGENT_TASK_COMPLETED',
  AGENT_TASK_FAILED:           'AGENT_TASK_FAILED',
  AGENT_LLM_CHUNK:             'AGENT_LLM_CHUNK',
  AGENT_LLM_MESSAGE:           'AGENT_LLM_MESSAGE',
  AGENT_TOOL_START:            'AGENT_TOOL_START',
  AGENT_TOOL_RESULT:           'AGENT_TOOL_RESULT',
  AGENT_TOOL_ERROR:            'AGENT_TOOL_ERROR',
  AGENT_STATE_CHANGE:          'AGENT_STATE_CHANGE',
  AGENT_HUMAN_INPUT_REQUIRED:  'AGENT_HUMAN_INPUT_REQUIRED',
  AGENT_WORKFLOW_TRANSITION:   'AGENT_WORKFLOW_TRANSITION',
}

/* ─── Delta filter: suppress zero-speed heartbeat spam ─────────── */
const lastTelemetry = new Map()  // assetId → { speed, lat, lng, ts }
const DELTA_THRESHOLD_METERS = 2

function hasMeaningfulDelta(assetId, telemetry) {
  const prev = lastTelemetry.get(assetId)
  if (!prev) {
    lastTelemetry.set(assetId, telemetry)
    return true
  }

  // Always pass if speed > 0
  if (telemetry.speed > 0) {
    lastTelemetry.set(assetId, telemetry)
    return true
  }

  // If stopped (speed === 0), only pass if position changed significantly
  const dlat = (telemetry.lat || 0) - (prev.lat || 0)
  const dlng = (telemetry.lng || 0) - (prev.lng || 0)
  const dist = Math.sqrt(dlat * dlat + dlng * dlng) * 111_139 // rough meters

  if (dist > DELTA_THRESHOLD_METERS) {
    lastTelemetry.set(assetId, telemetry)
    return true
  }

  // Throttle stopped heartbeats: pass one per 30 seconds
  const elapsed = Date.now() - (prev._ts || 0)
  if (elapsed > 30_000) {
    lastTelemetry.set(assetId, { ...telemetry, _ts: Date.now() })
    return true
  }

  return false
}

/* ═══════════════════════════════════════════════════════════════════
   Internal event dispatcher
   ═══════════════════════════════════════════════════════════════════ */

function dispatchEvent(eventType, payload) {
  // Global listeners
  const globalCbs = globalListeners.get(eventType)
  if (globalCbs) {
    globalCbs.forEach(cb => {
      try { cb(payload) } catch (e) { console.error('[Socket] listener error:', e) }
    })
  }

  // Room-scoped listeners
  const room = payload?._room
  if (room && roomListeners.has(room)) {
    const eventMap = roomListeners.get(room)
    const cbs = eventMap?.get(eventType)
    if (cbs) {
      cbs.forEach(cb => {
        try { cb(payload) } catch (e) { console.error('[Socket] room listener error:', e) }
      })
    }
  }
}

/* ═══════════════════════════════════════════════════════════════════
   Connection Management
   ═══════════════════════════════════════════════════════════════════ */

/**
 * Initialize the /ops namespace connection.
 * Call once from App.vue or after login.
 */
export function connectSocket() {
  if (opsSocket) return opsSocket   // already created (may still be connecting)

  const token = getToken()
  if (!token) {
    console.warn('[Socket] No auth token available, deferring connection')
    return null
  }

  console.log('[Socket] Connecting to', `${LIVE_URL}/ops`, '| token:', token ? `${token.slice(0, 12)}…` : 'MISSING', '| org:', getOrgId() || 'MISSING')

  opsSocket = io(`${LIVE_URL}/ops`, {
    auth: { token },
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 10_000,
    transports: ['polling', 'websocket'],   // polling first — more reliable for local dev
  })

  /* ─── Connection lifecycle ───────────────────────────────── */
  opsSocket.on('connect', () => {
    console.log('[Socket] Connected to /ops namespace')
    socketState.connected = true
    socketState.reconnectAttempt = 0
    socketState.error = null

    // Re-join all tracked rooms on reconnect (uses same event as joinRoom)
    joinedRooms.forEach(room => {
      opsSocket.emit('joinRoom', room)
      console.log(`[Socket] Re-joined room after reconnect: ${room}`)
    })

    // Auto-join org room (use Rooms.org() for consistent format)
    const orgId = getOrgId()
    if (orgId) {
      const orgRoom = Rooms.org(orgId)
      if (!joinedRooms.has(orgRoom)) {
        joinedRooms.add(orgRoom)
        opsSocket.emit('joinRoom', orgRoom)
        console.log(`[Socket] Auto-joined org room: ${orgRoom}`)
      }
    }
  })

  opsSocket.on('disconnect', (reason) => {
        console.log('[Socket] Disconnected:', reason)
        socketState.connected = false
  })

  opsSocket.on('connect_error', (error) => {
    console.error('[Socket] Connection error:', error.message)
    socketState.error = error.message
    socketState.reconnectAttempt++

    // If auth error, try refreshing token
    if (error.message?.includes('auth') || error.message?.includes('jwt')) {
      const freshToken = getToken()
      if (freshToken && opsSocket) {
        opsSocket.auth = { token: freshToken }
      }
    }
  })

  opsSocket.io.on('reconnect_attempt', (attempt) => {
    socketState.reconnectAttempt = attempt
    // Refresh token on each reconnect attempt
    opsSocket.auth = { token: getToken() }
  })

  opsSocket.io.on('reconnect', () => {
    console.log('[Socket] Reconnected successfully')
  })

  /* ─── Typed Event Handlers ───────────────────────────────── */

    // Device measurement — with delta filter (keyed by asset_id)
    opsSocket.on(Events.DEVICE_MEASUREMENT, (payload) => {
        const assetId = payload.asset_id || payload.device_id || payload.id
        logger.info(`[Socket] Received measurement for asset ${assetId}:`, payload)
        console.log(`[Socket] Received measurement for asset ${assetId}:`, payload)
        if (!assetId) return

        const telemetry = {
          lat: extractCoords(payload)?.[1] ?? payload.lat,
          lng: extractCoords(payload)?.[0] ?? payload.lng,
          speed: payload.payload?.['position.speed'] ?? payload.speed ?? 0,
          heading: payload.payload?.['position.direction'] ?? payload.heading ?? 0,
          _ts: Date.now(),
        }

        if (!hasMeaningfulDelta(assetId, telemetry)) return

        socketState.lastEvent = { type: Events.DEVICE_MEASUREMENT, assetId, ts: Date.now() }
        dispatchEvent(Events.DEVICE_MEASUREMENT, { ...payload, _parsed: telemetry })
    })

    // Device events (alerts) — always pass through (never filter)
    opsSocket.on(Events.DEVICE_EVENT, (payload) => {
        socketState.lastEvent = { type: Events.DEVICE_EVENT, ts: Date.now() }
        dispatchEvent(Events.DEVICE_EVENT, payload)
    })

    // Device status changes
    opsSocket.on(Events.DEVICE_STATUS, (payload) => {
        socketState.lastEvent = { type: Events.DEVICE_STATUS, ts: Date.now() }
        dispatchEvent(Events.DEVICE_STATUS, payload)
    })

    // Mission updates
    opsSocket.on(Events.MISSION_UPDATE, (payload) => {
        socketState.lastEvent = { type: Events.MISSION_UPDATE, ts: Date.now() }
        dispatchEvent(Events.MISSION_UPDATE, payload)
    })

    opsSocket.on(Events.MISSION_COMPLETE, (payload) => {
        socketState.lastEvent = { type: Events.MISSION_COMPLETE, ts: Date.now() }
        dispatchEvent(Events.MISSION_COMPLETE, payload)
    })

    // Narrative events — unified feed across agents
    opsSocket.on(Events.NARRATIVE_EVENT, (payload) => {
        socketState.lastEvent = { type: Events.NARRATIVE_EVENT, ts: Date.now() }
        dispatchEvent(Events.NARRATIVE_EVENT, payload)
    })

    // Agent status broadcasts
    opsSocket.on(Events.AGENT_STATUS, (payload) => {
        socketState.lastEvent = { type: Events.AGENT_STATUS, ts: Date.now() }
        dispatchEvent(Events.AGENT_STATUS, payload)
    })

    // Catch-all for ALL incoming events — DEBUG
    opsSocket.onAny((event, ...args) => {
        console.log(`[Socket] ⚡ IN « ${event} »`, args[0] ?? '')
        if (!Object.values(Events).includes(event) && !event.startsWith('room:')) {
          dispatchEvent(event, args[0])
        }
    })

    opsSocket.onAnyOutgoing((eventName, ...args) => {
        console.log(`[Socket] 📤 OUT « ${eventName} »`, args[0] ?? '')
    })




  return opsSocket
}

/**
 * Disconnect cleanly — call on logout.
 */
export function disconnectSocket() {
  if (opsSocket) {
    opsSocket.disconnect()
    opsSocket = null
  }
  joinedRooms.clear()
  roomListeners.clear()
  globalListeners.clear()
  lastTelemetry.clear()
  socketState.connected = false
  socketState.lastEvent = null
  socketState.error = null
}

/* ═══════════════════════════════════════════════════════════════════
   Room Management (4-level hierarchy)
   ═══════════════════════════════════════════════════════════════════ */

/**
 * Join a room. Use Room builders: Rooms.org('abc'), Rooms.asset('123')
 */
export function joinRoom(room) {
  if (joinedRooms.has(room)) {
    // Already tracking (and emitted) this room — skip duplicate emit
    return
  }
  joinedRooms.add(room)
  if (opsSocket?.connected) {
    opsSocket.emit('joinRoom', room)
    console.log('[Socket] Joined room:', room)
  } else {
    console.log('[Socket] Queued room (will join on connect):', room)
  }
}

/**
 * Leave a room and clean up its listeners.
 */
export function leaveRoom(room) {
  joinedRooms.delete(room)
  roomListeners.delete(room)
  if (opsSocket?.connected) {
    opsSocket.emit('room:leave', { room })
    console.debug('[Socket] Left room:', room)
  }
}

/**
 * Check if a room is currently joined.
 */
export function isInRoom(room) {
  return joinedRooms.has(room)
}

/* ═══════════════════════════════════════════════════════════════════
   Listener Registration
   ═══════════════════════════════════════════════════════════════════ */

/**
 * Subscribe to a typed event globally (across all rooms).
 * Returns an unsubscribe function.
 *
 * @param {string} eventType - One of Events.* constants
 * @param {Function} callback - (payload) => void
 * @returns {Function} unsubscribe
 */
export function onEvent(eventType, callback) {
  if (!globalListeners.has(eventType)) {
    globalListeners.set(eventType, new Set())
  }
  globalListeners.get(eventType).add(callback)

  return () => {
    globalListeners.get(eventType)?.delete(callback)
  }
}

/**
 * Subscribe to a typed event scoped to a specific room.
 * Returns an unsubscribe function.
 *
 * @param {string} room - Room ID (use Rooms.* builders)
 * @param {string} eventType - One of Events.* constants
 * @param {Function} callback - (payload) => void
 * @returns {Function} unsubscribe
 */
export function onRoomEvent(room, eventType, callback) {
  if (!roomListeners.has(room)) {
    roomListeners.set(room, new Map())
  }
  const eventMap = roomListeners.get(room)
  if (!eventMap.has(eventType)) {
    eventMap.set(eventType, new Set())
  }
  eventMap.get(eventType).add(callback)

  return () => {
    eventMap.get(eventType)?.delete(callback)
  }
}

/* ═══════════════════════════════════════════════════════════════════
   Message Sending
   ═══════════════════════════════════════════════════════════════════ */

/**
 * Emit a typed event to a room.
 */
export function emitToRoom(room, eventType, payload) {
  if (opsSocket?.connected) {
    opsSocket.emit(eventType, { ...payload, _room: room })
  } else {
    console.warn('[Socket] Cannot emit, not connected')
  }
}

/**
 * Send a command to a specific asset.
 */
export function sendAssetCommand(assetId, command, payload = {}) {
  emitToRoom(Rooms.asset(assetId), 'asset:command', {
    asset_id: assetId,
    command,
    ...payload,
  })
}

/* ═══════════════════════════════════════════════════════════════════
   Composable Helper (for use in Vue components)
   ═══════════════════════════════════════════════════════════════════ */

/**
 * useSocket() — composable for Vue components.
 * Auto-cleans up listeners on unmount.
 *
 * Usage:
 *   const { on, onRoom, isConnected } = useSocket()
 *   on(Events.DEVICE_MEASUREMENT, (data) => { ... })
 *   onRoom(Rooms.asset('abc'), Events.DEVICE_MEASUREMENT, (data) => { ... })
 */
export function useSocket() {
  const unsubs = []

  const on = (eventType, callback) => {
    unsubs.push(onEvent(eventType, callback))
  }

  const onRoom = (room, eventType, callback) => {
    unsubs.push(onRoomEvent(room, eventType, callback))
  }

  const isConnected = computed(() => socketState.connected)

  // Auto-cleanup when component unmounts
  if (getCurrentInstance()) {
    onUnmounted(() => {
      unsubs.forEach(fn => fn())
    })
  }

  return {
    on,
    onRoom,
    joinRoom,
    leaveRoom,
    emitToRoom,
    sendAssetCommand,
    isConnected,
    socketState,
  }
}

/* ═══════════════════════════════════════════════════════════════════
   Backward Compatibility (deprecated — remove in Phase 2)
   ═══════════════════════════════════════════════════════════════════ */

/** @deprecated Use joinRoom(Rooms.org(id)) instead */
export function listenRoom(room, event, callback) {
  console.warn('[Socket] listenRoom() is deprecated. Use onEvent() or onRoomEvent()')
  joinRoom(room)
  return onEvent(event, callback)
}

/** @deprecated Use onEvent() instead */
export function listenChannel(channel, event, callback) {
  console.warn('[Socket] listenChannel() is deprecated. Use onEvent()')
  return onEvent(channel, callback)
}

/** @deprecated Use emitToRoom() instead */
export function sendMessage(channel, message) {
  console.warn('[Socket] sendMessage() is deprecated. Use emitToRoom()')
  if (opsSocket?.connected) {
    opsSocket.emit('message', { channel, message })
  }
}

